import { useEffect, useState } from 'react';
import { Badge, Card, cx } from '../Ui';
import {
  deliveryTypeLabels,
  fulfillmentLabels,
  getCriticality,
  orderStatusLabels,
  orderStatusTone,
  summarizeItems,
} from './control-tower-utils';
import type { StatusTone, CriticalityLevel, OrderView } from './control-tower-utils';

const criticalityBorder: Record<CriticalityLevel, string> = {
  normal: 'border-emerald-200',
  warning: 'border-amber-300',
  critical: 'border-rose-300 ring-2 ring-rose-200',
};

const criticalityLabel: Record<CriticalityLevel, StatusTone> = {
  normal: 'green',
  warning: 'amber',
  critical: 'red',
};

interface ControlTowerOrderCardProps {
  order: OrderView;
  onSelectOrder(order: OrderView): void;
}

export function ControlTowerOrderCard({ order, onSelectOrder }: ControlTowerOrderCardProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const criticality = getCriticality(order, now);
  const hasFulfillment = order.fulfillmentStage !== null;

  return (
    <button type="button" className="w-full text-left" onClick={() => onSelectOrder(order)} aria-label={`Ver detalle del pedido #${String(order.correlation).padStart(2, '0')}`}>
    <Card className={cx('p-3.5 transition hover:border-accent hover:shadow-surface', criticalityBorder[criticality.level])}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-black text-foreground">
          #{String(order.correlation).padStart(2, '0')}
        </div>
        <div title={criticality.label} className={cx('rounded-full px-2 py-0.5 text-xs font-bold', criticalityBorder[criticality.level])}>
          {criticality.level === 'normal' ? 'Normal' : criticality.level === 'warning' ? 'Atención' : 'Retraso'}
        </div>
      </div>

      <div className="mt-2 text-sm font-semibold text-foreground" title={order.customerName}>
        {order.customerName || 'Cliente'}
      </div>
      <div className="mt-0.5 text-xs text-muted">{deliveryTypeLabels[order.deliveryType]}</div>

      <p className="mt-2 line-clamp-2 text-xs text-muted" title={summarizeItems(order)}>
        {summarizeItems(order)}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <Badge tone={orderStatusTone[order.status]}>{orderStatusLabels[order.status]}</Badge>
        {hasFulfillment && order.fulfillmentStage && (
          <Badge tone={fulfillmentTone(order.fulfillmentStage)}>
            {fulfillmentLabels[order.fulfillmentStage]}
          </Badge>
        )}
      </div>

      <div className="mt-2 text-right">
        <Badge tone={criticalityLabel[criticality.level]}>{criticality.label}</Badge>
      </div>
    </Card>
    </button>
  );
}

function fulfillmentTone(stage: OrderView['fulfillmentStage']): StatusTone {
  switch (stage) {
    case 'kitchen':
    case 'delivery_prep':
      return 'blue';
    case 'ready':
      return 'amber';
    case 'transit':
      return 'purple';
    case 'delivered':
    case 'handed_off':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'purple';
  }
}
